/**
 * A free-text box with a hard cap and a live counter.
 *
 * One component for both designs, because the rule is the same in both and
 * the failure mode of duplicating it is that one copy keeps counting past
 * the limit.
 *
 * The cap is enforced in the change handler as well as by `maxLength`.
 * `maxLength` does not apply to a paste in every engine, and it does not
 * apply at all to text set programmatically, so the attribute alone would
 * let a pasted brief through and then have the server truncate it in
 * silence.
 */

import React from 'react';
import { FREE_TEXT_MAX } from './audienceCopy';

// `forwardRef`, because callers focus the field: the landing page puts the
// caret back in it when an empty brief is submitted, and a plain function
// component would have swallowed that ref and failed silently.
const CountedTextarea = React.forwardRef(function CountedTextarea({
  value, onChange, max = FREE_TEXT_MAX, className, wrapClassName, counterClassName,
  children, ...rest
}, ref) {
  const text = String(value || '');
  const atLimit = text.length >= max;

  return (
    <div className={wrapClassName}>
      <textarea
        ref={ref}
        className={className}
        value={text}
        maxLength={max}
        onChange={(e) => onChange(e.target.value.slice(0, max))}
        {...rest}
      />
      {children}
      <p
        className={`${counterClassName}${atLimit ? ` ${counterClassName}--full` : ''}`}
        // Announced only when it matters. A counter that reads out on every
        // keystroke is unusable with a screen reader on.
        aria-live={atLimit ? 'polite' : 'off'}
      >
        {text.length}/{max}
      </p>
    </div>
  );
});

export default CountedTextarea;
